/**
 * Admin Onboarding Types
 * TASK-339: Onboarding Wizard UI
 */

// Token validation response
export interface TokenValidateResponse {
  valid: boolean;
  account_id?: string;
  company_name?: string;
  admin_email?: string;
  admin_first_name?: string;
  admin_last_name?: string;
  expires_at?: string;
  error?: string;
}

// Set password request/response
export interface SetPasswordRequest {
  token: string;
  password: string;
  first_name?: string;
  last_name?: string;
}

export interface SetPasswordResponse {
  success: boolean;
  message: string;
}

// Firm info request/response
export interface FirmInfoRequest {
  token: string;
  // Legal
  legal_company_name?: string;
  legal_form?: string;
  registration_number?: string;
  lei_code?: string;
  finma_license_number?: string;
  sro_membership?: string;
  // Address
  address_street?: string;
  address_city?: string;
  address_postal_code?: string;
  address_country?: string;
  // Contact
  main_phone?: string;
  website_url?: string;
  billing_email?: string;
  // Business
  industry_specialization?: string;
  employee_count_range?: string;
}

export interface FirmInfoResponse {
  success: boolean;
  message: string;
}

// Security settings
export type SecurityPreset = 'standard' | 'strict' | 'custom';

export interface SecuritySettingsRequest {
  token: string;
  preset?: SecurityPreset;
  min_password_length?: number;
  require_2fa_for_admin?: boolean;
  require_2fa_for_all?: boolean;
  session_timeout_minutes?: number;
  max_failed_attempts?: number;
}

export interface SecuritySettingsResponse {
  success: boolean;
  message: string;
}

// Team invitations
export interface TeamInvitation {
  email: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'advisor' | 'viewer' | 'compliance_officer';
}

export interface TeamInviteRequest {
  token: string;
  invitations: TeamInvitation[];
}

export interface TeamInviteResponse {
  success: boolean;
  sent_count: number;
  failed: string[];
  message: string;
}

// Complete onboarding
export interface CompleteOnboardingRequest {
  token: string;
}

export interface CompleteOnboardingResponse {
  success: boolean;
  access_token: string | null; // null when the tenant enforces 2FA (challenge owed)
  redirect_url: string;
  message: string;
  // TASK-631: present when the tenant requires 2FA at completion
  challenge_token?: string | null;
  status?: '2fa_required' | '2fa_enrollment_required' | null;
}

// Onboarding wizard state
export interface OnboardingState {
  token: string;
  accountId: string;
  companyName: string;
  adminEmail: string;
  adminFirstName: string;
  adminLastName: string;
  expiresAt: Date | null;
  // Step completion tracking
  passwordSet: boolean;
  firmInfoSaved: boolean;
  securityConfigured: boolean;
  teamInvited: boolean;
}

// Wizard step definition
export interface OnboardingStep {
  id: number;
  name: string;
  description: string;
  icon: string;
  required: boolean;
}

// All steps
export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 1,
    name: 'Set Password',
    description: 'Create a secure password for your account',
    icon: 'Lock',
    required: true,
  },
  {
    id: 2,
    name: 'Firm Information',
    description: 'Enter your company details',
    icon: 'Building2',
    required: false,
  },
  {
    id: 3,
    name: 'Security Settings',
    description: 'Configure security policies',
    icon: 'Shield',
    required: false,
  },
  {
    id: 4,
    name: 'Invite Team',
    description: 'Add your team members',
    icon: 'Users',
    required: false,
  },
];
